import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import aiChatbotsService, { AIChatbot } from "../../features/ai_chatbots/aiChatbotsService";
import "./personal-chat-modern.css";

const PersonalChatbotSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [chatbots, setChatbots] = useState<AIChatbot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await aiChatbotsService.getChatbots({ skip: 0, limit: 100, is_active: true });
        const list: AIChatbot[] = response?.data || response?.chatbots || [];
        setChatbots(list);
      } catch (err: any) {
        setError(err?.response?.data?.detail || t("personal_chatbots_load_failed"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatbots();
  }, [t]);

  const handleStartChat = (chatbot: AIChatbot) => {
    localStorage.setItem("personal_selected_chatbot_id", String(chatbot.id));
    navigate("/personal/chat", { state: { chatbotId: chatbot.id } });
  };

  return (
    <div className="pcx-shell">
      <div className="pcx-dashboard-head">
        <div>
          <h2 className="pcx-dashboard-title">{t("dashboard")}</h2>
          <p className="pcx-dashboard-subtitle">
            {t("personal_dashboard_subtitle")}
          </p>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {isLoading ? (
        <div className="d-flex align-items-center gap-2 mt-2">
          <Spinner animation="border" size="sm" />
          <span>{t("loading")}</span>
        </div>
      ) : null}

      {!isLoading && chatbots.length === 0 ? (
        <Alert variant="info">{t("personal_no_active_chatbots")}</Alert>
      ) : null}

      <Row className="g-3 mt-1">
        {chatbots.map((chatbot) => (
          <Col key={chatbot.id} md={6} lg={4}>
            <Card className="h-100 pcx-chatbot-card">
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Card.Title className="mb-0 pcx-chatbot-name">{chatbot.name}</Card.Title>
                  <Badge bg={chatbot.is_active ? "success" : "secondary"}>
                    {chatbot.is_active ? t("active") : t("inactive")}
                  </Badge>
                </div>
                <Card.Text className="pcx-chatbot-desc">
                  {chatbot.description || t("no_description")}
                </Card.Text>
                <div className="mt-auto">
                  <button type="button" className="pcx-btn pcx-primary-btn" onClick={() => handleStartChat(chatbot)}>
                    {t("start_chat")}
                  </button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default PersonalChatbotSelectionPage;
